import { z } from 'zod';

// ============================================================================
// Pricing Rules (P2-PRC-002)
// ============================================================================

export const PricingConditionSchema = z.object({
  type: z.string().min(1, 'condition type is required'),
  min: z.number().optional(),
  max: z.number().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  days: z.array(z.number()).optional(),
  ticketTypeIds: z.array(z.string()).optional(),
});

export type PricingCondition = z.infer<typeof PricingConditionSchema>;

export const CreatePricingRuleRequestSchema = z.object({
  name: z.string().min(1, 'name is required'),
  description: z.string().optional(),
  priority: z.number().int().min(1).max(100),
  conditions: z.array(PricingConditionSchema),
  multiplierAdjustment: z.number().min(-1.0).max(1.0),
  validFrom: z.string().date(),
  validUntil: z.string().date().optional(),
  isActive: z.boolean().optional(),
  createdBy: z.string().optional(),
});

export type CreatePricingRuleRequest = z.infer<typeof CreatePricingRuleRequestSchema>;

export const UpdatePricingRuleRequestSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  priority: z.number().int().min(1).max(100).optional(),
  conditions: z.array(PricingConditionSchema).optional(),
  multiplierAdjustment: z.number().min(-1.0).max(1.0).optional(),
  validFrom: z.string().date().optional(),
  validUntil: z.string().date().optional(),
  isActive: z.boolean().optional(),
  updatedBy: z.string().optional(),
});

export type UpdatePricingRuleRequest = z.infer<typeof UpdatePricingRuleRequestSchema>;

export const ListRulesQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  isActive: z
    .string()
    .transform((v) => (v === 'true' ? true : v === 'false' ? false : undefined))
    .optional(),
});

export type ListRulesQuery = z.infer<typeof ListRulesQuerySchema>;

// ============================================================================
// Response schemas — Prisma PricingRule serialised as JSON
// ============================================================================

// Single rule record returned by create / update / getById / list items.
// `conditions` is an opaque JSON value — consumers type-narrow as needed.
export const PricingRuleRecordSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  priority: z.number().int(),
  conditions: z.unknown(),
  multiplierAdjustment: z.number(),
  validFrom: z.string().date(),
  validUntil: z.string().date().optional(),
  isActive: z.boolean(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type PricingRuleRecord = z.infer<typeof PricingRuleRecordSchema>;

// GET /admin/pricing/rules  (paginated list)
export const ListRulesResponseSchema = z.object({
  data: z.array(PricingRuleRecordSchema),
  meta: z.object({
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    limit: z.number().int().positive(),
    totalPages: z.number().int().nonnegative(),
  }),
});

export type ListRulesResponse = z.infer<typeof ListRulesResponseSchema>;
